import { onBeforeUnmount, ref } from 'vue'
import type { Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  addExternalVideo as addExternalVideoRequest,
  bulkDeleteGalleryItems,
  bulkMoveGalleryItems,
  bulkSetItemsHidden,
  deleteGalleryItem,
  getGallery,
  listGalleryItems,
  reorderGalleryItems,
  retryGalleryItem,
  updateGallery,
  updateGalleryItem,
} from '@/api/galleries'
import { CoverMediaStatus, GalleryItemStatus, GalleryStatus } from '@/types/galleries'
import type {
  ExternalVideoPayload,
  OrganizerGallery,
  OrganizerGalleryItem,
  UpdateItemPayload,
} from '@/types/galleries'
import { useGalleryToast } from '@/composables/useGalleryToast'
import { useGalleryUploadManager } from '@/composables/useGalleryUploadManager'
import { parseGalleryApiError } from '@/utils/galleries/apiError'
import { pollWithBackoff } from '@/utils/galleries/pollBackoff'
import { moveById } from '@/utils/galleries/reorder'
import type { MoveTarget } from '@/utils/galleries/reorder'

export const ITEM_REORDER_DEBOUNCE_MS = 500
const QUIET_REFRESH_DEBOUNCE_MS = 800

export type GalleryLoadError = 'not_found' | 'generic' | null

export interface ItemSaveResult {
  item: OrganizerGalleryItem | null
  errors: Record<string, string[]>
  code: string | null
}

export interface BulkMoveResult {
  ok: boolean
  code: string | null
}

export interface UseGallery {
  gallery: Ref<OrganizerGallery | null>
  items: Ref<OrganizerGalleryItem[]>
  loading: Ref<boolean>
  error: Ref<GalleryLoadError>
  busy: Ref<boolean>
  refresh: (quiet?: boolean) => Promise<void>
  updateItem: (itemId: string, payload: UpdateItemPayload) => Promise<ItemSaveResult>
  setHidden: (ids: string[], hidden: boolean) => Promise<boolean>
  deleteItem: (itemId: string) => Promise<boolean>
  bulkDelete: (ids: string[]) => Promise<boolean>
  bulkMove: (ids: string[], targetGalleryId: string) => Promise<BulkMoveResult>
  moveItem: (itemId: string, target: MoveTarget) => void
  reorderItems: (ids: string[]) => void
  setCover: (itemId: string | null) => Promise<boolean>
  retryItem: (itemId: string) => Promise<boolean>
  addExternalVideo: (payload: ExternalVideoPayload) => Promise<ItemSaveResult>
  togglePublished: () => Promise<boolean>
}

export function useGallery(eventSlug: string, galleryId: string): UseGallery {
  const { t } = useI18n()
  const toast = useGalleryToast()
  const manager = useGalleryUploadManager()

  const gallery = ref<OrganizerGallery | null>(null)
  const items = ref<OrganizerGalleryItem[]>([])
  const loading = ref(false)
  const error = ref<GalleryLoadError>(null)
  const busy = ref(false)

  let reorderTimer: ReturnType<typeof setTimeout> | null = null
  let reorderSnapshot: OrganizerGalleryItem[] | null = null
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  let stopPolling: (() => void) | null = null
  let stopCoverPolling: (() => void) | null = null

  function applyGallery(next: OrganizerGallery): void {
    const { items: incomingItems, ...meta } = next
    gallery.value = meta
    if (incomingItems !== undefined && reorderTimer === null) {
      items.value = incomingItems
    }
    ensurePolling()
    ensureCoverPolling()
  }

  function isCoverBusy(next: OrganizerGallery | null): boolean {
    return (
      next?.cover_media_status === CoverMediaStatus.Pending ||
      next?.cover_media_status === CoverMediaStatus.Processing
    )
  }

  /** Polls the gallery while the dedicated cover of an external gallery is still being processed. */
  function ensureCoverPolling(): void {
    if (stopCoverPolling !== null || !isCoverBusy(gallery.value)) return
    stopCoverPolling = pollWithBackoff(
      async () => {
        const fresh = await getGallery(eventSlug, galleryId)
        applyGallery(fresh)
        return !isCoverBusy(fresh)
      },
      {
        onFinish: () => {
          stopCoverPolling = null
        },
      },
    )
  }

  async function refresh(quiet = false): Promise<void> {
    if (!quiet) {
      loading.value = true
      error.value = null
    }
    try {
      applyGallery(await getGallery(eventSlug, galleryId))
    } catch (e) {
      if (!quiet) {
        error.value = parseGalleryApiError(e).status === 404 ? 'not_found' : 'generic'
      }
    } finally {
      if (!quiet) loading.value = false
    }
  }

  function scheduleQuietRefresh(): void {
    if (refreshTimer !== null) clearTimeout(refreshTimer)
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      void refresh(true)
    }, QUIET_REFRESH_DEBOUNCE_MS)
  }

  function replaceItem(next: OrganizerGalleryItem): void {
    const index = items.value.findIndex((i) => i.id === next.id)
    if (index === -1) {
      items.value = [...items.value, next]
    } else {
      items.value = items.value.map((i) => (i.id === next.id ? next : i))
    }
  }

  function ensurePolling(): void {
    if (stopPolling !== null) return
    if (!items.value.some((i) => i.status === GalleryItemStatus.Processing)) return
    stopPolling = pollWithBackoff(
      async () => {
        const ids = items.value.filter((i) => i.status === GalleryItemStatus.Processing).map((i) => i.id)
        if (ids.length === 0) return true
        const fresh = await listGalleryItems(eventSlug, galleryId, ids.slice(0, 100))
        let changed = false
        for (const item of fresh) {
          const before = items.value.find((i) => i.id === item.id)
          if (before !== undefined && before.status !== item.status) changed = true
          replaceItem(item)
        }
        if (changed) scheduleQuietRefresh()
        return !items.value.some((i) => i.status === GalleryItemStatus.Processing)
      },
      {
        onFinish: () => {
          stopPolling = null
        },
      },
    )
  }

  const offCreated = manager.onItemCreated((entry, item) => {
    if (entry.eventSlug !== eventSlug || entry.galleryId !== galleryId) return
    replaceItem(item)
    ensurePolling()
    scheduleQuietRefresh()
  })
  const offUpdated = manager.onItemUpdated((entry, item) => {
    if (entry.eventSlug !== eventSlug || entry.galleryId !== galleryId) return
    replaceItem(item)
    scheduleQuietRefresh()
  })

  async function updateItem(itemId: string, payload: UpdateItemPayload): Promise<ItemSaveResult> {
    busy.value = true
    try {
      const item = await updateGalleryItem(eventSlug, galleryId, itemId, payload)
      replaceItem(item)
      scheduleQuietRefresh()
      return { item, errors: {}, code: null }
    } catch (e) {
      const parsed = parseGalleryApiError(e)
      if (parsed.status !== 422) toast.push(t('galleries.errors.generic'), { kind: 'error' })
      return { item: null, errors: parsed.errors, code: parsed.code }
    } finally {
      busy.value = false
    }
  }

  async function setHidden(ids: string[], hidden: boolean): Promise<boolean> {
    const previous = items.value
    items.value = items.value.map((i) => (ids.includes(i.id) ? { ...i, is_hidden: hidden } : i))
    try {
      const changed = await bulkSetItemsHidden(eventSlug, galleryId, ids, hidden)
      for (const item of changed) replaceItem(item)
      scheduleQuietRefresh()
      return true
    } catch {
      items.value = previous
      toast.push(t('galleries.errors.generic'), { kind: 'error' })
      return false
    }
  }

  async function deleteItem(itemId: string): Promise<boolean> {
    return bulkDelete([itemId], true)
  }

  async function bulkDelete(ids: string[], single = false): Promise<boolean> {
    busy.value = true
    try {
      if (single && ids.length === 1 && ids[0] !== undefined) {
        await deleteGalleryItem(eventSlug, galleryId, ids[0])
      } else {
        await bulkDeleteGalleryItems(eventSlug, galleryId, ids)
      }
      items.value = items.value.filter((i) => !ids.includes(i.id))
      scheduleQuietRefresh()
      return true
    } catch {
      toast.push(t('galleries.errors.delete_items_failed'), { kind: 'error' })
      return false
    } finally {
      busy.value = false
    }
  }

  async function bulkMove(ids: string[], targetGalleryId: string): Promise<BulkMoveResult> {
    busy.value = true
    try {
      await bulkMoveGalleryItems(eventSlug, galleryId, targetGalleryId, ids)
      items.value = items.value.filter((i) => !ids.includes(i.id))
      scheduleQuietRefresh()
      return { ok: true, code: null }
    } catch (e) {
      const parsed = parseGalleryApiError(e)
      return { ok: false, code: parsed.code }
    } finally {
      busy.value = false
    }
  }

  async function flushReorder(): Promise<void> {
    reorderTimer = null
    const snapshot = reorderSnapshot
    reorderSnapshot = null
    try {
      const fresh = await reorderGalleryItems(eventSlug, galleryId, items.value.map((i) => i.id))
      for (const item of fresh) replaceItem(item)
    } catch (e) {
      if (snapshot !== null) items.value = snapshot
      const parsed = parseGalleryApiError(e)
      toast.push(
        parsed.status === 422 ? t('galleries.errors.reorder_stale') : t('galleries.errors.reorder_failed'),
        { kind: 'error' },
      )
      if (parsed.status === 422) void refresh(true)
    }
  }

  /** Optimistic reorder: applied at once, sent after 500 ms of quiet, rolled back on failure. */
  function reorderItems(ids: string[]): void {
    if (reorderSnapshot === null) reorderSnapshot = [...items.value]
    const byId = new Map(items.value.map((i) => [i.id, i]))
    items.value = ids.flatMap((id) => {
      const item = byId.get(id)
      return item === undefined ? [] : [item]
    })
    if (reorderTimer !== null) clearTimeout(reorderTimer)
    reorderTimer = setTimeout(() => {
      void flushReorder()
    }, ITEM_REORDER_DEBOUNCE_MS)
  }

  function moveItem(itemId: string, target: MoveTarget): void {
    reorderItems(moveById(items.value, itemId, target).map((i) => i.id))
  }

  async function setCover(itemId: string | null): Promise<boolean> {
    try {
      const next = await updateGallery(eventSlug, galleryId, { cover_item_id: itemId })
      if (gallery.value !== null) gallery.value = { ...gallery.value, ...next, items: undefined }
      return true
    } catch (e) {
      const parsed = parseGalleryApiError(e)
      toast.push(
        parsed.code === 'invalid_cover' ? t('galleries.errors.invalid_cover') : t('galleries.errors.generic'),
        { kind: 'error' },
      )
      return false
    }
  }

  async function retryItem(itemId: string): Promise<boolean> {
    try {
      const item = await retryGalleryItem(eventSlug, galleryId, itemId)
      replaceItem(item)
      ensurePolling()
      return true
    } catch {
      toast.push(t('galleries.errors.retry_failed'), { kind: 'error' })
      return false
    }
  }

  async function addExternalVideo(payload: ExternalVideoPayload): Promise<ItemSaveResult> {
    busy.value = true
    try {
      const item = await addExternalVideoRequest(eventSlug, galleryId, payload)
      replaceItem(item)
      scheduleQuietRefresh()
      return { item, errors: {}, code: null }
    } catch (e) {
      const parsed = parseGalleryApiError(e)
      if (parsed.status !== 422) toast.push(t('galleries.errors.generic'), { kind: 'error' })
      return { item: null, errors: parsed.errors, code: parsed.code }
    } finally {
      busy.value = false
    }
  }

  async function togglePublished(): Promise<boolean> {
    if (gallery.value === null) return false
    const next = gallery.value.status === GalleryStatus.Published ? GalleryStatus.Draft : GalleryStatus.Published
    try {
      const updated = await updateGallery(eventSlug, galleryId, { status: next })
      gallery.value = { ...gallery.value, ...updated, items: undefined }
      return true
    } catch {
      toast.push(t('galleries.errors.generic'), { kind: 'error' })
      return false
    }
  }

  onBeforeUnmount(() => {
    offCreated()
    offUpdated()
    stopPolling?.()
    stopCoverPolling?.()
    if (refreshTimer !== null) clearTimeout(refreshTimer)
    if (reorderTimer !== null) {
      clearTimeout(reorderTimer)
      void flushReorder()
    }
  })

  return {
    gallery,
    items,
    loading,
    error,
    busy,
    refresh,
    updateItem,
    setHidden,
    deleteItem,
    bulkDelete,
    bulkMove,
    moveItem,
    reorderItems,
    setCover,
    retryItem,
    addExternalVideo,
    togglePublished,
  }
}
